import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, deleteField, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import { createNotification } from '../services/NotificationSystem';
import { 
    Users, UserPlus, UserMinus, Clock, CheckCircle, XCircle,
    Mail, Phone, Calendar, Shield, AlertTriangle, RefreshCw,
    Building2, Search
} from 'lucide-react';

function CompanyManager({ user }) {
    const [activeTab, setActiveTab] = useState('current');
    const [staffList, setStaffList] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteMessage, setInviteMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        if (user && !user.companyId) {
            fetchData();
        }
    }, [user]);

    const fetchData = async () => {
        try {
            setLoading(true);
            await Promise.all([
                fetchCurrentStaff(),
                fetchPendingRequests()
            ]);
        } catch (error) {
            console.error('데이터 로딩 실패:', error);
            setMessage({ type: 'error', text: '데이터를 불러오는데 실패했습니다.' });
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentStaff = async () => {
        try {
            const staffQuery = query(
                collection(db, 'users'),
                where('companyId', '==', user.uid)
            );
            const snapshot = await getDocs(staffQuery);
            
            const staff = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                joinedAt: doc.data().approvedAt || doc.data().updatedAt
            }));
            setStaffList(staff);
            console.log('현재 직원:', staff.length, '명');
        } catch (error) {
            console.error('직원 목록 조회 실패:', error);
        }
    };

    const fetchPendingRequests = async () => {
        try {
            // 1. 기존 방식: users 컬렉션의 pendingCompanyId
            const pendingQuery = query(
                collection(db, 'users'),
                where('pendingCompanyId', '==', user.uid)
            );
            const snapshot = await getDocs(pendingQuery);

            const oldRequests = snapshot.docs.map(d => {
                const u = d.data();
                return {
                    staffUid: d.id,
                    hasLegacy: true,
                    applicationIds: [],
                    name: u.name || u.displayName || '',
                    email: u.email || '',
                    phone: u.phone || '',
                    requestedAt: u.requestedAt || u.staffJoinRequestedAt || u.updatedAt || null,
                };
            });

            // 2. 새 방식: staffApplications 컬렉션
            //   신청 문서 스키마가 둘이다 — applicantId·applicantName(앱 staff_application_screen) / staffId·staffName(웹 StaffAffiliation·앱 affiliation).
            //   (2026-10-12) 종전엔 ...doc.data() 를 마지막에 펼쳐 문서의 type:'staffApplication' 이 카드 type 을 덮어써
            //   웹 신청 카드가 legacy 로 처리됐다(신청 문서 id 를 users id 로 씀) → 필드를 명시적으로 뽑는다.
            let newRequests = [];
            try {
                const appQuery = query(
                    collection(db, 'staffApplications'),
                    where('companyId', '==', user.uid),
                    where('status', '==', 'pending')
                );
                const appSnapshot = await getDocs(appQuery);
                newRequests = appSnapshot.docs.map(d => {
                    const a = d.data();
                    return {
                        staffUid: a.applicantId || a.staffId || null,
                        hasLegacy: false,
                        applicationIds: [d.id],
                        name: a.applicantName || a.staffName || '',
                        email: a.applicantEmail || a.staffEmail || '',
                        phone: a.applicantPhone || a.staffPhone || '',
                        requestedAt: a.createdAt?.toDate?.() || a.createdAt || null,
                    };
                });
            } catch (e) {
                console.error('staffApplications 조회 실패:', e?.code || e?.message || e);
            }

            // 3. 같은 신청자(uid)의 legacy·신청 문서 카드를 1장으로 합친다 (점검 ⑩ A-3 — 같은 신청이 2장으로 보이던 문제)
            const merged = new Map();
            [...oldRequests, ...newRequests].forEach(r => {
                const key = r.staffUid || `app:${r.applicationIds[0]}`;
                const cur = merged.get(key);
                if (!cur) { merged.set(key, { ...r, id: key }); return; }
                cur.hasLegacy = cur.hasLegacy || r.hasLegacy;
                cur.applicationIds = [...cur.applicationIds, ...r.applicationIds];
                cur.name = cur.name || r.name;
                cur.email = cur.email || r.email;
                cur.phone = cur.phone || r.phone;
                cur.requestedAt = cur.requestedAt || r.requestedAt;
            });
            const allRequests = [...merged.values()].map(r => ({ ...r, type: r.applicationIds.length ? 'application' : 'legacy' }));
            setPendingRequests(allRequests);
            console.log('대기 중인 요청:', allRequests.length, '건 (legacy:', oldRequests.length, ', new:', newRequests.length, ')');
        } catch (error) {
            console.error('대기 요청 조회 실패:', error);
        }
    };

    // 서버 오류 문구 — 서버가 준 한국어 사유를 그대로 보여준다. 함수 미배포 등 영문 코드만 오면 대체 문구
    const serverReason = (e, fallback) => {
        const m = e?.message || '';
        if (/[가-힣]/.test(m)) return m;
        const code = e?.code || '';
        if (code.includes('not-found')) return '승인 기능이 아직 서버에 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.';
        if (code.includes('unauthenticated')) return '로그인이 필요합니다. 다시 로그인해 주세요.';
        return fallback;
    };

    // 직원 승인 (통합) — 서버 approveStaffApplication 이 소속 필드·신청 문서 정리·알림·감사기록을 한 번에 처리한다 (2026-10-12)
    //   신청 문서가 있는 카드 → { applicationId }, users.pendingCompanyId 만 있는 카드 → { staffUid }
    const approveStaff = async (request) => {
        if (!window.confirm('이 직원을 승인하시겠습니까?')) return;
        const payload = request.applicationIds?.length
            ? { applicationId: request.applicationIds[0] }
            : { staffUid: request.staffUid };
        if (!payload.applicationId && !payload.staffUid) {
            setMessage({ type: 'error', text: '신청자 정보를 찾을 수 없습니다. 새로고침 후 다시 시도해 주세요.' });
            return;
        }
        try {
            setProcessing(true);
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'approveStaffApplication');
            const res = await fn(payload);
            const r = res?.data || {};
            const who = request.name || request.email || '신청자';
            setMessage({
                type: 'success',
                text: r.already ? `${who}님은 이미 직원으로 등록되어 있습니다. 남은 신청을 정리했습니다.` : `${who}님을 직원으로 승인했습니다.`
            });
            await fetchData();
        } catch (error) {
            console.error('직원 승인 실패:', error?.code || '', error?.details?.code || '', error?.message || '');
            setMessage({ type: 'error', text: `직원 승인에 실패했습니다: ${serverReason(error, '잠시 후 다시 시도해 주세요.')}` });
            await fetchData();   // 이미 처리된 신청 등 — 목록을 서버 상태로 맞춘다
        } finally {
            setProcessing(false);
        }
    };

    // 직원 요청 거절 (통합) — 합친 카드면 신청 문서들과 users.pendingCompanyId 를 함께 정리한다.
    //   rules: staffApplications update = 수신 업체(companyId==나) 허용 / users 2번 절 = pendingCompanyId==나 이고 pending 필드만 지우면 허용
    //   (pendingStaff 는 2번 절 hasOnly 밖이라 건드리지 않는다)
    const rejectStaff = async (request) => {
        if (!window.confirm('이 요청을 거절하시겠습니까?')) return;
        setProcessing(true);
        const failed = [];
        let done = 0;
        for (const appId of (request.applicationIds || [])) {
            try {
                await updateDoc(doc(db, 'staffApplications', appId), {
                    status: 'rejected',
                    rejectedAt: new Date(),
                    updatedAt: new Date()
                });
                done += 1;
            } catch (e) {
                console.error('신청 문서 거절 실패:', appId, e?.code || e?.message || e);
                failed.push('신청 문서');
            }
        }
        if (request.hasLegacy && request.staffUid) {
            try {
                await updateDoc(doc(db, 'users', request.staffUid), {
                    pendingCompanyId: deleteField(),
                    pendingCompanyName: deleteField(),
                    pendingCompanyType: deleteField(),
                    requestedAt: deleteField()
                });
                done += 1;
            } catch (e) {
                console.error('신청 표시(pendingCompanyId) 정리 실패:', e?.code || e?.message || e);
                failed.push('신청 표시');
            }
        }
        if (done > 0 && request.staffUid) {
            // createNotification 은 실패를 자체 기록(console.error)하고 { success:false } 를 돌려준다
            const n = await createNotification({
                recipientId: request.staffUid,
                type: 'staff_rejected',
                title: '직원 요청이 거절되었습니다',
                message: `${user.businessName}에서 직원 요청을 거절했습니다.`,
                link: '/profile'
            });
            if (!n?.success) console.error('거절 알림 전송 실패 — 신청자:', request.staffUid);
        }
        if (failed.length === 0) {
            setMessage({ type: 'success', text: '요청을 거절했습니다.' });
        } else if (done > 0) {
            setMessage({ type: 'error', text: `거절은 처리됐지만 일부(${failed.join(', ')})를 정리하지 못했습니다. 새로고침 후 다시 거절해 주세요.` });
        } else {
            setMessage({ type: 'error', text: '요청 거절에 실패했습니다.' });
        }
        try {
            await fetchData();
        } finally {
            setProcessing(false);
        }
    };

    const removeStaff = async (staffId, staffName) => {
        if (!window.confirm(`${staffName}님을 직원에서 제거하시겠습니까?\n제거 후에는 회사 데이터에 접근할 수 없습니다.`)) {
            return;
        }

        try {
            setProcessing(true);
            console.log('직원 제거 시작:', staffId);

            await updateDoc(doc(db, 'users', staffId), {
                userType: 'individual',
                memberType: 'individual',
                
                companyId: deleteField(),
                companyName: deleteField(),
                companyBusinessType: deleteField(),
                businessType: deleteField(),
                businessName: deleteField(),
                approvedAt: deleteField(),
                status: deleteField(),
                role: deleteField(),
                
                removedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            setMessage({ 
                type: 'success', 
                text: `${staffName}님이 직원에서 제거되었습니다.` 
            });

            try {
                await createNotification({
                    recipientId: staffId,
                    type: 'staff_removed',
                    title: '직원에서 제거되었습니다',
                    message: `${user.businessName}에서 직원 권한이 해제되었습니다.`,
                    link: '/profile'
                });
            } catch (e) {
                console.log('알림 전송 실패:', e);
            }

            await fetchData();
        } catch (error) {
            console.error('직원 제거 실패:', error);
            setMessage({ type: 'error', text: '직원 제거에 실패했습니다.' });
        } finally {
            setProcessing(false);
        }
    };

    const sendInvitation = async (e) => {
        e.preventDefault();
        if (!inviteEmail) {
            setMessage({ type: 'error', text: '초대할 직원의 이메일을 입력하세요.' });
            return;
        }

        try {
            setProcessing(true);

            // 🔐 2026-10-03 보안 P0-1: users 이메일 검색은 rules 에서 막혔다 → 서버 조회(CF findUserByEmail).
            //   대상이 환자면 결과 없음, 반환은 최소 필드(uid·businessType·userType·hasCompany·inMyCompany).
            let found = [];
            try {
                const lookup = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'findUserByEmail');
                const res = await lookup({ email: inviteEmail.trim(), mode: 'exact' });
                found = (res.data && res.data.results) || [];
            } catch (lookupErr) {
                console.error('회원 조회 실패:', lookupErr);
                setMessage({ type: 'error', text: ['functions/resource-exhausted', 'functions/invalid-argument'].includes(lookupErr?.code)
                    ? (lookupErr.message || '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.')
                    : '회원 조회에 실패했습니다. 잠시 후 다시 시도해 주세요.' });
                return;
            }

            if (found.length === 0) {
                setMessage({
                    type: 'warning',
                    text: '해당 이메일로 가입된 회원이 없습니다.\n먼저 회원가입을 안내해주세요.'
                });
                return;
            }

            const targetData = found[0];
            const targetUserId = targetData.uid;

            // 🔥 개인회원(individual)은 허용, 실제 업체 대표만 차단
            const isBusinessOwner = targetData.businessType && !targetData.hasCompany &&
                (targetData.businessType === 'dental' || targetData.businessType === 'lab' || targetData.businessType === 'seller');
            const isSellerOrSupplier = targetData.userType === 'seller' || targetData.userType === 'supplier';

            if (isBusinessOwner || isSellerOrSupplier) {
                setMessage({
                    type: 'error',
                    text: '치과/기공소/판매처 대표 계정은 직원으로 초대할 수 없습니다.\n개인 계정 이메일을 입력해주세요.'
                });
                return;
            }

            if (targetData.inMyCompany) {
                setMessage({ type: 'warning', text: '이미 우리 회사 직원입니다.' });
                return;
            }

            if (targetData.hasCompany) {
                setMessage({ type: 'error', text: '다른 회사에 소속된 직원입니다.' });
                return;
            }

            if (!targetUserId) {
                setMessage({ type: 'error', text: '사용자 정보를 가져올 수 없습니다.' });
                return;
            }

            const companyDoc = await getDoc(doc(db, 'users', user.uid));
            const companyData = companyDoc.data();
            const companyName = companyData?.businessName || companyData?.name || user.businessName || '회사';

            // 🔥 이미 보낸 초대가 있는지 확인
            const existingInviteQuery = query(
                collection(db, 'staffInvites'),
                where('email', '==', inviteEmail.trim().toLowerCase()),
                where('companyId', '==', user.uid),
                where('status', '==', 'pending')
            );
            const existingInvites = await getDocs(existingInviteQuery);
            if (!existingInvites.empty) {
                setMessage({ type: 'warning', text: '이미 해당 이메일로 초대를 보냈습니다.' });
                return;
            }

            // 🔥 staffInvites 컬렉션에 초대 생성
            await addDoc(collection(db, 'staffInvites'), {
                email: inviteEmail.trim().toLowerCase(),
                companyId: user.uid,
                companyName: companyName,
                companyBusinessType: companyData?.businessType || user.businessType,
                message: inviteMessage || '저희 업체의 직원으로 함께 일하고 싶습니다.',
                status: 'pending',
                createdAt: serverTimestamp(),
                createdBy: user.uid
            });

            try {
                await createNotification({
                    recipientId: targetUserId,
                    type: 'staff_invitation',
                    title: '직원 초대',
                    message: `${companyName}에서 직원으로 초대했습니다.`,
                    link: '/staff-invitations'
                });
            } catch (e) {
                console.log('알림 전송 실패:', e);
            }

            setMessage({
                type: 'success',
                text: `${inviteEmail}에게 초대를 전송했습니다.\n상대방이 [받은 초대] 메뉴에서 승인하면 직원으로 등록됩니다.`
            });
            setInviteEmail('');
            setInviteMessage('');
        } catch (error) {
            console.error('초대 실패:', error);
            setMessage({ type: 'error', text: '초대 전송에 실패했습니다: ' + error.message });
        } finally {
            setProcessing(false);
        }
    };

    const filteredStaff = staffList.filter(staff =>
        staff.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staff.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (date) => {
        if (!date) return '-';
        if (date.toDate) return date.toDate().toLocaleDateString('ko-KR');
        if (typeof date === 'string') return new Date(date).toLocaleDateString('ko-KR');
        if (date instanceof Date) return date.toLocaleDateString('ko-KR');
        return '-';
    };

    if (user?.companyId) {
        return (
            <div style={styles.accessDenied}>
                <AlertTriangle size={48} color="#ef4444" />
                <h2>접근 권한 없음</h2>
                <p>업체 관리는 회사 대표만 사용할 수 있습니다.</p>
            </div>
        );
    }

    if (!user?.businessType) {
        return (
            <div style={styles.noCompany}>
                <Building2 size={48} color="#6366f1" />
                <h2>업체 등록이 필요합니다</h2>
                <p>업체 관리를 위해서는 먼저 업체를 등록해주세요.</p>
                <button 
                    onClick={() => window.location.href = '/company-register'}
                    style={styles.registerButton}
                >
                    업체 등록하기
                </button>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={styles.loading}>
                <RefreshCw size={32} className="spin" />
                <p>로딩 중...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerContent}>
                    <h1 style={styles.title}>
                        <Building2 size={28} />
                        업체 관리
                    </h1>
                    <p style={styles.subtitle}>
                        {user.businessName}의 직원을 관리합니다
                    </p>
                </div>
                <div style={styles.stats}>
                    <div style={styles.statItem}>
                        <div style={styles.statValue}>{staffList.length}</div>
                        <div style={styles.statLabel}>현재 직원</div>
                    </div>
                    <div style={styles.statItem}>
                        <div style={{...styles.statValue, color: pendingRequests.length > 0 ? '#f59e0b' : '#6366f1'}}>
                            {pendingRequests.length}
                        </div>
                        <div style={styles.statLabel}>대기 요청</div>
                    </div>
                </div>
            </div>

            {message.text && (
                <div style={{
                    ...styles.message,
                    ...(message.type === 'success' ? styles.successMessage : {}),
                    ...(message.type === 'error' ? styles.errorMessage : {}),
                    ...(message.type === 'warning' ? styles.warningMessage : {})
                }}>
                    {message.type === 'success' && <CheckCircle size={18} />}
                    {message.type === 'error' && <XCircle size={18} />}
                    {message.type === 'warning' && <AlertTriangle size={18} />}
                    {message.text}
                </div>
            )}

            <div style={styles.tabs}>
                <button
                    style={{
                        ...styles.tab,
                        ...(activeTab === 'current' ? styles.activeTab : {})
                    }}
                    onClick={() => setActiveTab('current')}
                >
                    <Users size={18} />
                    현재 직원 ({staffList.length})
                </button>
                <button
                    style={{
                        ...styles.tab,
                        ...(activeTab === 'pending' ? styles.activeTab : {})
                    }}
                    onClick={() => setActiveTab('pending')}
                >
                    <Clock size={18} />
                    가입 신청 ({pendingRequests.length})
                    {pendingRequests.length > 0 && (
                        <span style={styles.badge}>{pendingRequests.length}</span>
                    )}
                </button>
                <button
                    style={{
                        ...styles.tab,
                        ...(activeTab === 'invite' ? styles.activeTab : {})
                    }}
                    onClick={() => setActiveTab('invite')}
                >
                    <UserPlus size={18} />
                    직원 초대
                </button>
            </div>

            {activeTab === 'current' && (
                <div style={styles.tabContent}>
                    <div style={styles.searchBar}>
                        <Search size={18} color="#64748b" />
                        <input
                            type="text"
                            placeholder="이름 또는 이메일로 검색..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>

                    {filteredStaff.length === 0 ? (
                        <div style={styles.emptyState}>
                            <Users size={48} color="#cbd5e1" />
                            <p>등록된 직원이 없습니다</p>
                        </div>
                    ) : (
                        <div style={styles.staffGrid}>
                            {filteredStaff.map(staff => (
                                <div key={staff.id} style={styles.staffCard}>
                                    <div style={styles.staffHeader}>
                                        <div style={styles.staffAvatar}>
                                            {staff.name?.[0] || staff.email?.[0]}
                                        </div>
                                        <div style={styles.staffInfo}>
                                            <h3 style={styles.staffName}>
                                                {staff.name || '이름 없음'}
                                            </h3>
                                            <p style={styles.staffEmail}>
                                                <Mail size={14} />
                                                {staff.email}
                                            </p>
                                            {staff.phone && (
                                                <p style={styles.staffPhone}>
                                                    <Phone size={14} />
                                                    {staff.phone}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div style={styles.staffMeta}>
                                        <div style={styles.staffJoined}>
                                            <Calendar size={14} />
                                            가입일: {formatDate(staff.joinedAt)}
                                        </div>
                                        <div style={styles.staffRole}>
                                            <Shield size={14} />
                                            직원
                                        </div>
                                    </div>

                                    {/* 정산 권한 — 제거됨 */}

                                    <div style={styles.staffActions}>
                                        <button
                                            onClick={() => removeStaff(staff.id, staff.name || staff.email)}
                                            style={styles.removeButton}
                                            disabled={processing}
                                        >
                                            <UserMinus size={16} />
                                            제거
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'pending' && (
                <div style={styles.tabContent}>
                    {pendingRequests.length === 0 ? (
                        <div style={styles.emptyState}>
                            <Clock size={48} color="#cbd5e1" />
                            <p>대기 중인 가입 신청이 없습니다</p>
                        </div>
                    ) : (
                        <div style={styles.requestList}>
                            {pendingRequests.map(request => (
                                <div key={request.id} style={styles.requestCard}>
                                    <div style={styles.requestInfo}>
                                        <div style={styles.requestHeader}>
                                            <h4 style={styles.requestName}>
                                                {request.name || request.applicantName || '이름 없음'}
                                            </h4>
                                            {request.type === 'application' && (
                                                <span style={styles.newBadge}>신규 신청</span>
                                            )}
                                        </div>
                                        <p style={styles.requestEmail}>
                                            <Mail size={14} />
                                            {request.email || request.applicantEmail}
                                        </p>
                                        {(request.phone || request.applicantPhone) && (
                                            <p style={styles.requestPhone}>
                                                <Phone size={14} />
                                                {request.phone || request.applicantPhone}
                                            </p>
                                        )}
                                        <p style={styles.requestDate}>
                                            <Calendar size={14} />
                                            신청일: {formatDate(request.requestedAt || request.createdAt)}
                                        </p>
                                    </div>
                                    <div style={styles.requestActions}>
                                        <button
                                            onClick={() => approveStaff(request)}
                                            style={styles.approveButton}
                                            disabled={processing}
                                        >
                                            <CheckCircle size={18} />
                                            승인
                                        </button>
                                        <button
                                            onClick={() => rejectStaff(request)}
                                            style={styles.rejectButton}
                                            disabled={processing}
                                        >
                                            <XCircle size={18} />
                                            거절
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'invite' && (
                <div style={styles.tabContent}>
                    <div style={styles.inviteSection}>
                        <h3 style={styles.sectionTitle}>이메일로 직원 초대</h3>
                        <p style={styles.sectionDesc}>
                            DentConnect에 가입한 <strong>개인 회원</strong>을 직원으로 초대할 수 있습니다.
                        </p>
                        
                        <div style={styles.warningBox}>
                            <AlertTriangle size={18} color="#f59e0b" />
                            <span>치과/기공소/판매처 계정은 초대할 수 없습니다. 개인 계정만 가능합니다.</span>
                        </div>
                        
                        <form onSubmit={sendInvitation} style={styles.inviteForm}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>초대할 직원 이메일</label>
                                <input
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="개인 계정 이메일 입력"
                                    style={styles.input}
                                    required
                                />
                            </div>
                            
                            <div style={styles.formGroup}>
                                <label style={styles.label}>초대 메시지 (선택)</label>
                                <textarea
                                    value={inviteMessage}
                                    onChange={(e) => setInviteMessage(e.target.value)}
                                    placeholder="함께 일하게 되어 기쁩니다!"
                                    style={styles.textarea}
                                    rows="3"
                                />
                            </div>
                            
                            <button
                                type="submit"
                                style={styles.inviteButton}
                                disabled={processing}
                            >
                                {processing ? '전송 중...' : '초대 보내기'}
                            </button>
                        </form>

                        <div style={styles.inviteInfo}>
                            <h4>직원 등록 방법</h4>
                            <ol style={styles.processList}>
                                <li><strong>방법 1:</strong> 직원이 직접 [직원신청] 메뉴에서 업체 검색 후 가입 신청</li>
                                <li><strong>방법 2:</strong> 위 입력란에 직원 이메일 입력 후 초대</li>
                                <li>신청/초대 후 이 페이지 [가입 신청] 탭에서 승인</li>
                            </ol>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '24px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #e2e8f0',
        flexWrap: 'wrap',
        gap: '20px',
    },
    headerContent: {
        flex: 1,
        minWidth: '200px',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        margin: '0 0 8px 0',
        fontSize: '28px',
        fontWeight: '700',
        color: '#0f172a',
    },
    subtitle: {
        margin: 0,
        fontSize: '15px',
        color: '#64748b',
    },
    stats: {
        display: 'flex',
        gap: '32px',
    },
    statItem: {
        textAlign: 'center',
    },
    statValue: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#6366f1',
    },
    statLabel: {
        fontSize: '13px',
        color: '#64748b',
        marginTop: '4px',
    },
    message: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 18px',
        borderRadius: '8px',
        marginBottom: '24px',
        fontSize: '14px',
        fontWeight: '500',
        whiteSpace: 'pre-line',
    },
    successMessage: {
        backgroundColor: '#d1fae5',
        color: '#065f46',
        border: '1px solid #10b981',
    },
    errorMessage: {
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: '1px solid #ef4444',
    },
    warningMessage: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        border: '1px solid #f59e0b',
    },
    warningBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px',
        backgroundColor: '#fef3c7',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '13px',
        color: '#92400e',
        border: '1px solid #fbbf24',
    },
    tabs: {
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '1px solid #e2e8f0',
        flexWrap: 'wrap',
    },
    tab: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 20px',
        backgroundColor: 'transparent',
        color: '#64748b',
        border: 'none',
        borderBottom: '2px solid transparent',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
    },
    activeTab: {
        color: '#6366f1',
        borderBottom: '2px solid #6366f1',
    },
    badge: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        minWidth: '18px',
        height: '18px',
        padding: '0 6px',
        backgroundColor: '#ef4444',
        color: 'white',
        borderRadius: '9px',
        fontSize: '11px',
        fontWeight: '700',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    newBadge: {
        padding: '2px 8px',
        backgroundColor: '#dbeafe',
        color: '#1d4ed8',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '600',
    },
    tabContent: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #e2e8f0',
    },
    searchBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        marginBottom: '24px',
    },
    searchInput: {
        flex: 1,
        border: 'none',
        backgroundColor: 'transparent',
        fontSize: '14px',
        outline: 'none',
    },
    staffGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '20px',
    },
    staffCard: {
        padding: '20px',
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
    },
    staffHeader: {
        display: 'flex',
        gap: '16px',
        marginBottom: '16px',
    },
    staffAvatar: {
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        backgroundColor: '#6366f1',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        fontWeight: '700',
        flexShrink: 0,
    },
    staffInfo: {
        flex: 1,
        minWidth: 0,
    },
    staffName: {
        margin: '0 0 4px 0',
        fontSize: '16px',
        fontWeight: '700',
        color: '#0f172a',
    },
    staffEmail: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        margin: '0 0 4px 0',
        fontSize: '13px',
        color: '#64748b',
    },
    staffPhone: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        margin: 0,
        fontSize: '13px',
        color: '#64748b',
    },
    staffMeta: {
        display: 'flex',
        gap: '16px',
        paddingTop: '12px',
        borderTop: '1px solid #e2e8f0',
        marginBottom: '12px',
    },
    staffJoined: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        color: '#64748b',
    },
    staffRole: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        color: '#64748b',
    },
    staffActions: {
        display: 'flex',
        gap: '8px',
    },
    removeButton: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: 'none',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    requestList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    requestCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px',
        backgroundColor: '#fffbeb',
        borderRadius: '10px',
        border: '1px solid #fbbf24',
        flexWrap: 'wrap',
        gap: '16px',
    },
    requestInfo: {
        flex: 1,
        minWidth: '200px',
    },
    requestHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '8px',
    },
    requestName: {
        margin: 0,
        fontSize: '16px',
        fontWeight: '700',
        color: '#0f172a',
    },
    requestEmail: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        margin: '0 0 4px 0',
        fontSize: '14px',
        color: '#64748b',
    },
    requestPhone: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        margin: '0 0 4px 0',
        fontSize: '14px',
        color: '#64748b',
    },
    requestDate: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        margin: 0,
        fontSize: '12px',
        color: '#94a3b8',
    },
    requestActions: {
        display: 'flex',
        gap: '12px',
    },
    approveButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 20px',
        backgroundColor: '#10b981',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    rejectButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 20px',
        backgroundColor: '#ef4444',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    inviteSection: {
        maxWidth: '600px',
        margin: '0 auto',
    },
    sectionTitle: {
        margin: '0 0 8px 0',
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a',
    },
    sectionDesc: {
        margin: '0 0 16px 0',
        fontSize: '14px',
        color: '#64748b',
    },
    inviteForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        marginBottom: '32px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#1e293b',
    },
    input: {
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
    },
    textarea: {
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        resize: 'vertical',
        outline: 'none',
    },
    inviteButton: {
        padding: '14px 24px',
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    inviteInfo: {
        padding: '20px',
        backgroundColor: '#f0f9ff',
        borderRadius: '10px',
        border: '1px solid #bae6fd',
    },
    processList: {
        margin: '12px 0 0 0',
        paddingLeft: '20px',
        fontSize: '14px',
        color: '#0369a1',
        lineHeight: '1.8',
    },
    permissionRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderTop: '1px solid #f1f5f9',
    },
    permissionLabel: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#475569',
    },
    toggleButton: {
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background-color 0.2s',
        padding: 0,
    },
    toggleKnob: {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        position: 'absolute',
        top: '2px',
        left: '2px',
        transition: 'transform 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
        color: '#64748b',
    },
    accessDenied: {
        textAlign: 'center',
        padding: '80px 20px',
        maxWidth: '400px',
        margin: '0 auto',
    },
    noCompany: {
        textAlign: 'center',
        padding: '80px 20px',
        maxWidth: '400px',
        margin: '0 auto',
    },
    registerButton: {
        marginTop: '20px',
        padding: '12px 32px',
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    loading: {
        textAlign: 'center',
        padding: '80px 20px',
        color: '#64748b',
    },
};

export default CompanyManager;